// Lib
import { FC, useState } from "react";
import { Upload as AntDUpload } from "antd";
// Api
import { useUploadMediaDataMutation } from "rtkQuery/query/uploadMediaAPI";
// Hooks
import { useNotification, useViewport } from "hooks";
// Theme
import { theme } from "theme";
// Helpers
import {
  getMaxSizeTitle,
  validateImageDimensions,
  validateImageFileSize,
} from "./helpers";
// Utils
import { s3Upload } from "utils/s3Upload";
import { errorHandler } from "utils/errorHandler";
// Icons
import { FileUploadIcon, TrashIcon } from "icons";
// Components
import { Loader } from "components";
// Styled
import { FlexContainer } from "styled/Box";
import { Button } from "styled/Buttons";
import { Typography } from "styled/Typography";
import { ErrorMessage } from "../styled";
import {
  ActionsContainer,
  Copy,
  IconContainer,
  StyledImage,
  Wrapper,
} from "./styles";

const { Dragger } = AntDUpload;

interface ImageUploadProps {
  label?: string;
  disabled?: boolean;
  value?: string | null;
  error?: string | boolean;
  dimensions?: [number, number];
  maxSize?: number; // KB
  accept?: ("jpg" | "jpeg" | "png" | "webp")[];
  onChange?: (args: string | null) => void;
  style?: React.CSSProperties;
}

export const ImageUpload: FC<ImageUploadProps> = ({
  label,
  disabled,
  error,
  accept,
  dimensions,
  maxSize,
  value,
  onChange,
  style,
}) => {
  const { isMobile } = useViewport();
  const { openNotification } = useNotification();

  const [validationError, setValidationError] = useState("");

  const [getUploadLink] = useUploadMediaDataMutation();

  const [isLoading, setIsLoading] = useState(false);

  const handleUpload = async (file: Blob) => {
    if (!onChange) return;

    const validationErrors = [];

    if (dimensions) {
      const isValid = await validateImageDimensions(file, dimensions);

      if (!isValid) {
        validationErrors.push(
          `Image dimensions exceed acceptable values (${dimensions[0]}x${dimensions[1]})`,
        );
      }
    }

    if (maxSize) {
      const isValid = validateImageFileSize(file, maxSize);

      if (!isValid) {
        validationErrors.push(
          `The file is too big. ${getMaxSizeTitle(maxSize)}`,
        );
      }
    }

    if (validationErrors.length) {
      setValidationError(validationErrors.join(". "));

      return false;
    } else {
      setValidationError("");
    }

    try {
      setIsLoading(true);

      const response = await getUploadLink({
        data: {
          fileName: file instanceof File ? file.name : "fileUnknown",
          fileType: file.type,
        },
      }).unwrap();

      const url = response?.url;

      await s3Upload({ url, file });

      if (url) {
        const fileUrl = url.split("?")[0];
        onChange(fileUrl || null);
      }
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(false);
    }

    return false;
    // It is important to return false so that the download is not sent to the server
  };

  const acceptMimeTypes = accept?.length
    ? accept.map(type => `image/${type}`).join(", ")
    : "image/*";

  const validationsHintText = [
    getMaxSizeTitle(maxSize),
    dimensions ? `Dimensions: ${dimensions[0]}x${dimensions[1]}` : undefined,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Wrapper $error={!!error || !!validationError}>
      {isLoading && <Loader />}

      {!!label && <Typography.Label>{label}</Typography.Label>}

      {value ? (
        <StyledImage src={value} style={style} />
      ) : (
        <Dragger
          accept={acceptMimeTypes}
          multiple={false}
          disabled={disabled}
          showUploadList={false}
          beforeUpload={handleUpload}
          style={style}
        >
          <FlexContainer $column $align="center" $justify="center" $gap={16}>
            <IconContainer $align="center" $justify="center">
              <FileUploadIcon fill={theme.color.text.tertiary} />
            </IconContainer>

            {!isMobile && (
              <Copy>
                Click here to upload, or drag and drop
                {!validationError && validationsHintText && (
                  <>
                    <br />
                    {validationsHintText}
                  </>
                )}
                {!!validationError && !isMobile && (
                  <>
                    <ErrorMessage>{validationError}</ErrorMessage>
                  </>
                )}
              </Copy>
            )}
          </FlexContainer>
        </Dragger>
      )}

      {!!value && (
        <ActionsContainer>
          <AntDUpload
            accept={acceptMimeTypes}
            multiple={false}
            showUploadList={false}
            beforeUpload={handleUpload}
          >
            {!disabled && (
              <Button.SquaredIcon
                icon={<FileUploadIcon fill={theme.color.text.tertiary} />}
                disabled={disabled}
              />
            )}
          </AntDUpload>

          {!disabled && !!onChange && (
            <Button.SquaredIcon
              icon={<TrashIcon fill={theme.color.status.danger} />}
              disabled={disabled}
              onClick={() => onChange(null)}
            />
          )}
        </ActionsContainer>
      )}

      {isMobile && <Copy>{validationsHintText}</Copy>}
      {!!error && <ErrorMessage>{error}</ErrorMessage>}
      {isMobile && !!validationError && (
        <ErrorMessage>{validationError}</ErrorMessage>
      )}
    </Wrapper>
  );
};
